import { LinearProgress, Stack, TextField } from '@mui/material';
import { FC, useEffect } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { z } from 'zod';
import { useDebounceEffect } from '../utils/useDebounceEffect';
import { ControlledFormField } from './ControlledFormField';
import { useTypedForm } from '../../../shared/fe/hooks/useForm';

const createNameSchema = (intl: IntlShape) =>
	z.object({
		value: z.string().min(1, intl.formatMessage({ id: 'opportunity.detail.name.error.empty-name' })),
	});

type NameInputProps = {
	value?: string;
	isLoading: boolean;
	memoizedOnChange: (value: string) => void;
	placeholder: string;
};

export const NameInput: FC<NameInputProps> = ({ value, isLoading, memoizedOnChange, placeholder }) => {
	const intl = useIntl();

	const form = useTypedForm({
		schema: createNameSchema(intl),
		mode: 'all',
		defaultValues: {
			value: intl.formatMessage({ id: 'common.loading' }),
		},
	});

	useEffect(() => {
		if (!isLoading) {
			form.reset({
				value: value ?? '',
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	const { value: newValue } = form.watch();
	const { isDirty, isValid } = form.formState;

	useDebounceEffect(() => {
		// TODO: This is a hotfix. Come up with a way to use form.errors properly
		const isValidFix = newValue !== '';
		if (isDirty && isValid && isValidFix && value !== newValue) {
			memoizedOnChange(newValue);
		}
	}, [newValue, isDirty, isValid, memoizedOnChange]);

	return (
		<Stack sx={{ position: 'relative' }}>
			<ControlledFormField
				form={form}
				name={'value'}
				label={''}
				render={(props) => (
					<TextField
						{...props}
						size={'medium'}
						placeholder={placeholder}
						variant={'standard'}
						disabled={isLoading}
						sx={{
							width: '100%',
							'&>.MuiInputBase-root': {
								fontSize: '24px',
								fontWeight: 700,
								'&::before': {
									content: 'none',
								},
							},
						}}
					/>
				)}
			/>
			{isLoading && (
				<LinearProgress
					sx={{
						position: 'absolute',
						bottom: 0,
						width: '100%',
						height: 4,
						borderTopRightRadius: 0,
						borderTopLeftRadius: 0,
					}}
				/>
			)}
		</Stack>
	);
};
